/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Select, Button, Tag, DatePicker } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell, LabelList, PieChart, Pie } from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const NAVY = '#1a3a5c';
const toN  = v => Number(v || 0);
const fmtE = v => `E ${toN(v).toLocaleString()}`;

function KPI({ label, value, color = NAVY, sub }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 18px', borderTop:`3px solid ${color}`, flex:1 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
        </div>
    );
}
function ChartCard({ title, children, height = 280 }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10 }}>{title}</div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

// Decision matrix thresholds
function getDecision(repairPct, ageVsLifespan) {
    if (repairPct >= 75 || ageVsLifespan >= 90) return { d:'REPLACE',    color:'#f5222d', icon:'🔴' };
    if (repairPct >= 50 || ageVsLifespan >= 70) return { d:'REVIEW',     color:'#fa8c16', icon:'🟠' };
    if (repairPct >= 25 || ageVsLifespan >= 50) return { d:'MONITOR',    color:'#faad14', icon:'🟡' };
    return                                              { d:'KEEP',       color:'#52c41a', icon:'🟢' };
}

export default function FleetAnalyticsRepairReplace() {
    const [chronic, setChronic] = useState([]);
    const [tco,     setTco]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [region,    setRegion]   = useState('');
    const [programme, setProgramme]= useState('');
    const [facility,  setFacility] = useState('');
    const [vehType,   setVehType]  = useState('');
    const [dateFrom,  setDateFrom] = useState('');
    const [dateTo,    setDateTo]   = useState('');
    const [months,  setMonths]  = useState(12);

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                FleetService.reportChronic(months, 1),
                FleetService.reportTCO(),
            ]);
            if (Number(r1?.data?.code) === 200) setChronic(r1.data.result || []);
            if (Number(r2?.data?.code) === 200) setTco(r2.data.result     || []);
        } catch (e) { message.error('Failed to load data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, [months]);

    const regions = useMemo(() => [...new Set(tco.map(r => r.region).filter(Boolean))], [tco]);

    // Merge chronic + TCO for complete picture
    const analysis = useMemo(() => {
        return tco
            .filter(r => region ? r.region === region : true)
            .map(r => {
                const chr = chronic.find(c => c.registration_no === r.registration_no);
                const repairPct   = toN(r.procurement_cost) > 0
                    ? (toN(r.repair_cost) / toN(r.procurement_cost) * 100) : 0;
                const age         = r.installation_date
                    ? Math.floor((Date.now()-new Date(r.installation_date)) / 3.156e10) : 0;
                const lifespan    = toN(r.lifespanvalue) || 10;
                const ageVsLife   = Math.min(100, (age / lifespan) * 100);
                const repairCount = toN(chr?.repair_count);
                const decision    = getDecision(repairPct, ageVsLife);
                return {
                    reg:        r.registration_no,
                    type:       r.vehicle_type,
                    region:     r.region,
                    programme:  r.assigned_programme,
                    facility:   r.facility,
                    procurement:toN(r.procurement_cost),
                    repairCost: toN(r.repair_cost),
                    repairPct,
                    age, lifespan, ageVsLife,
                    repairCount,
                    totalCost:  toN(r.total_cost),
                    ...decision,
                };
            });
    }, [tco, chronic, region]);

    // Summary
    const replaceCount = analysis.filter(v => v.d === 'REPLACE').length;
    const reviewCount  = analysis.filter(v => v.d === 'REVIEW').length;
    const monitorCount = analysis.filter(v => v.d === 'MONITOR').length;
    const keepCount    = analysis.filter(v => v.d === 'KEEP').length;
    const totalRepairCost = analysis.reduce((s,v) => s + v.repairCost, 0);

    // Pie by decision
    const pie = [
        { name:'Replace', value:replaceCount,  fill:'#f5222d' },
        { name:'Review',  value:reviewCount,   fill:'#fa8c16' },
        { name:'Monitor', value:monitorCount,  fill:'#faad14' },
        { name:'Keep',    value:keepCount,      fill:'#52c41a' },
    ].filter(d => d.value > 0);

    // Top repair cost vehicles
    const topRepair = useMemo(() =>
        [...analysis].sort((a,b) => b.repairCost - a.repairCost).slice(0,12)
    , [analysis]);

    // By vehicle type decision
    const byType = useMemo(() => {
        const m = {};
        analysis.forEach(v => {
            const k = v.type || 'Other';
            if (!m[k]) m[k] = { name:k, Replace:0, Review:0, Monitor:0, Keep:0 };
            m[k][v.d]++;
        });
        return Object.values(m).sort((a,b) => (b.Replace+b.Review) - (a.Replace+a.Review));
    }, [analysis]);

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(analysis.map(v => ({
            'Reg No':         v.reg,
            'Type':           v.type,
            'Region':         v.region,
            'Programme':      v.programme,
            'Age (yrs)':      v.age,
            'Lifespan (yrs)': v.lifespan,
            'Age % of Life':  v.ageVsLife.toFixed(1),
            'Procurement (E)':v.procurement,
            'Repair Cost (E)':v.repairCost,
            'Repair % of Procurement': v.repairPct.toFixed(1),
            'Repair Count':   v.repairCount,
            'Decision':       v.d,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Repair vs Replace');
        saveAs(new Blob([XLSX.write(wb, { bookType:'xlsx', type:'array' })]),
            `Fleet_RepairReplace_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Repair vs Replace Decision Matrix</h4>
                    <div style={{ fontSize:13, color:'#888', marginTop:2 }}>
                        Cost-benefit analysis · chronic breakdown identification · fleet renewal planning
                    </div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <span style={{ fontSize:12, color:'#888' }}>Period:</span>
                    <Select value={months} onChange={v => setMonths(v)} style={{ width:120 }} size="small">
                        {[3,6,12,24].map(m => <Select.Option key={m} value={m}>{m} months</Select.Option>)}
                    </Select>
                    <Select allowClear placeholder="All Regions" style={{ width:130 }} size="small"
                        value={region||undefined} onChange={v=>setRegion(v||'')}>
                        {regions.map(r => <Select.Option key={r} value={r}>{r}</Select.Option>)}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={load} size="small"/>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={exportXlsx}
                        disabled={!analysis.length} size="small">Export</Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {/* KPIs */}
                <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                    <KPI label="Vehicles Analysed" value={analysis.length}        color={NAVY}/>
                    <KPI label="🔴 Replace"         value={replaceCount}           color="#f5222d" sub="immediate action"/>
                    <KPI label="🟠 Review"          value={reviewCount}            color="#fa8c16" sub="plan replacement"/>
                    <KPI label="🟡 Monitor"         value={monitorCount}           color="#faad14" sub="track condition"/>
                    <KPI label="🟢 Keep"            value={keepCount}              color="#52c41a" sub="good condition"/>
                    <KPI label="Total Repair Cost"  value={fmtE(totalRepairCost)}  color="#722ed1"/>
                </div>

                {/* Decision matrix criteria */}
                <div style={{ background:'#f6f8fa', border:'1px solid #e8e8e8', borderRadius:8,
                    padding:'10px 16px', marginBottom:14, fontSize:11, color:'#666',
                    display:'flex', gap:24, flexWrap:'wrap' }}>
                    <span>Decision criteria:</span>
                    <span>🔴 <strong>Replace</strong> — repair cost ≥75% of procurement OR age ≥90% of lifespan</span>
                    <span>🟠 <strong>Review</strong>  — repair ≥50% OR age ≥70%</span>
                    <span>🟡 <strong>Monitor</strong> — repair ≥25% OR age ≥50%</span>
                    <span>🟢 <strong>Keep</strong>    — below all thresholds</span>
                </div>

                {/* Row 1 */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:14 }}>
                    <ChartCard title="Decision Summary" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pie} dataKey="value" nameKey="name"
                                    cx="50%" cy="50%" outerRadius={100}
                                    label={({ name, value, percent }) =>
                                        value > 0 ? `${name}: ${value} (${(percent*100).toFixed(0)}%)` : ''}>
                                    {pie.map((d,i) => <Cell key={i} fill={d.fill}/>)}
                                </Pie>
                                <Tooltip/>
                                <Legend wrapperStyle={{ fontSize:11 }}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Top Vehicles by Repair Cost vs Procurement (E)" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topRepair.map(v=>({
                                    name:v.reg||'—', procurement:v.procurement, repair:v.repairCost, decision:v.d }))}
                                layout="vertical" margin={{ top:0, right:60, left:80, bottom:0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false}/>
                                <XAxis type="number" tick={{ fontSize:10 }} tickFormatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={75}/>
                                <Tooltip formatter={(v,n)=>[`E ${toN(v).toLocaleString()}`, n]}/>
                                <Legend wrapperStyle={{ fontSize:10 }}/>
                                <Bar dataKey="procurement" name="Procurement" fill={NAVY}    stackId="a"/>
                                <Bar dataKey="repair"      name="Repair Cost" fill="#f5222d" stackId="a" radius={[0,3,3,0]}>
                                    <LabelList dataKey="repair" position="right" style={{ fontSize:9 }}
                                        formatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* By vehicle type */}
                <ChartCard title="Decision Distribution by Vehicle Type" height={240}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byType} margin={{ top:5, right:10, left:10, bottom:20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                            <XAxis dataKey="name" tick={{ fontSize:10 }} angle={-20} textAnchor="end" height={40}/>
                            <YAxis tick={{ fontSize:10 }} allowDecimals={false}/>
                            <Tooltip/>
                            <Legend wrapperStyle={{ fontSize:10 }}/>
                            <Bar dataKey="Replace" stackId="a" fill="#f5222d"/>
                            <Bar dataKey="Review"  stackId="a" fill="#fa8c16"/>
                            <Bar dataKey="Monitor" stackId="a" fill="#faad14"/>
                            <Bar dataKey="Keep"    stackId="a" fill="#52c41a" radius={[4,4,0,0]}/>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                {/* Replace list */}
                {replaceCount > 0 && (
                    <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:8, padding:'12px 16px', marginTop:4 }}>
                        <div style={{ fontWeight:600, color:'#cf1322', marginBottom:8 }}>
                            🔴 Vehicles Recommended for Immediate Replacement ({replaceCount})
                        </div>
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                                <thead>
                                    <tr style={{ background:'#fff1f0', borderBottom:'1px solid #ffa39e' }}>
                                        {['Reg No','Type','Region','Age','Repair/Procurement','Repair Count','Reason'].map(h =>
                                            <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontWeight:600 }}>{h}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {analysis.filter(v => v.d==='REPLACE').map((v,i) => (
                                        <tr key={i} style={{ borderBottom:'1px solid #ffe8e8' }}>
                                            <td style={{ padding:'6px 10px', fontWeight:600 }}>{v.reg}</td>
                                            <td style={{ padding:'6px 10px' }}>{v.type}</td>
                                            <td style={{ padding:'6px 10px' }}>{v.region||'—'}</td>
                                            <td style={{ padding:'6px 10px' }}>{v.age} yrs</td>
                                            <td style={{ padding:'6px 10px' }}>
                                                <Tag color="red">{v.repairPct.toFixed(0)}%</Tag>
                                            </td>
                                            <td style={{ padding:'6px 10px' }}>{v.repairCount || 0}</td>
                                            <td style={{ padding:'6px 10px', color:'#cf1322', fontSize:11 }}>
                                                {v.repairPct>=75?'Repair costs excessive ':''}
                                                {v.ageVsLife>=90?'Exceeded lifespan ':''}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {!loading && analysis.length === 0 && (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa',
                        border:'2px dashed #e8e8e8', borderRadius:8 }}>
                        No data available. Record procurement costs and maintenance entries first.
                    </div>
                )}
            </Spin>
        </div>
    );
}